import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiService } from './api.service';

/**
 * Project A (YPM Portal) agent-signup integration — Project B side.
 *
 * SECURITY: the frontend never calls Project A (newportalapi.ypmgroup.com.au)
 * directly. Project A requires the `X-Service-Key` header — a shared SERVER
 * secret. Putting it in browser code would leak it. So every method here hits
 * Project B's OWN backend (the existing /api/v1 base via ApiService). Project
 * B's backend injects `X-Service-Key` + `X-Acting-Admin-Email` server-side and
 * forwards the call to Project A.
 *
 * ── Proxy contract for the backend team ────────────────────────────────────
 * 4 thin proxy routes under `<API_BASE>{{ PROXY_BASE }}`. Each one:
 *   1. authenticates the Project B admin (existing JWT),
 *   2. adds headers  X-Service-Key: <secret>
 *                    X-Acting-Admin-Email: <logged-in admin email>
 *                    X-Acting-Admin-Name:  <logged-in admin name>  (optional)
 *   3. forwards to  https://newportalapi.ypmgroup.com.au/api/melbourne-agent...
 *   4. returns Project A's JSON body + HTTP status unchanged.
 *
 *   Project B proxy route                  → Project A endpoint
 *   GET    {{ PROXY_BASE }}?status=&page=&limit=  → GET    {BASE}/signup-requests
 *   POST   {{ PROXY_BASE }}/:id/approve           → POST   {BASE}/signup-requests/:id/approve
 *   POST   {{ PROXY_BASE }}/:id/reject            → POST   {BASE}/signup-requests/:id/reject
 *   DELETE {{ PROXY_BASE }}/:id                   → DELETE {BASE}/signup-requests/:id
 *
 * Testing note: Project A scopes the list to the acting admin. Only
 * daniel@ypmgroup.com.au has seed data, so during testing the proxy should
 * send X-Acting-Admin-Email: daniel@ypmgroup.com.au or the list is empty.
 * ───────────────────────────────────────────────────────────────────────────
 */

/** The single place the proxy path is defined. Full path: /api/v1/melbourne-signups */
const PROXY_BASE = '/melbourne-signups';

export type SignupStatus = 'pending' | 'approved' | 'rejected' | 'all';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected';
export type CrmSyncStatus = 'pending' | 'synced' | 'failed' | null;

export interface SignupRequest {
  _id: string;
  name: string;
  companyName: string;
  mobile: string;
  email: string;
  approvalStatus: ApprovalStatus;
  requestStatus: string;
  crmSyncStatus: CrmSyncStatus;
  dynamicsContactId: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  lastError?: string | null;
  createdAt: string;
}

export interface SignupPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface SignupRequestsData {
  requests: SignupRequest[];
  pendingCount: number;
  pagination: SignupPagination;
}

/** Every Project A endpoint returns this envelope. */
export interface SignupApiResponse<T = any> {
  status: boolean;
  message: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class SignupRequestsService {
  constructor(private api: ApiService) {}

  /**
   * Emits when something outside the popup (e.g. the dashboard "Agent Portal
   * Approval" link) asks the signup-requests popup to open. The popup lives
   * globally in the header; subscribers there react to this.
   */
  private readonly openRequested$ = new Subject<void>();
  readonly open$: Observable<void> = this.openRequested$.asObservable();

  /** Ask the signup-requests popup to open on demand. */
  requestOpen(): void {
    this.openRequested$.next();
  }

  /**
   * Latest known pending signup-request count. The popup publishes it after
   * every fetch/poll; the dashboard subscribes to show/hide its
   * "Agent Portal Approval" link. Replays the last value to new subscribers.
   */
  private readonly pendingCount$$ = new BehaviorSubject<number>(0);
  readonly pendingCount$: Observable<number> = this.pendingCount$$.asObservable();

  /** Publish the latest pending count (called by the popup). */
  setPendingCount(count: number): void {
    this.pendingCount$$.next(count);
  }

  /** 1. List signup requests for the acting admin. Defaults to pending. */
  list(status: SignupStatus = 'pending', page = 1, limit = 20): Observable<SignupApiResponse<SignupRequestsData>> {
    return this.api.get(PROXY_BASE, {
      status,
      page: String(page),
      limit: String(limit),
    });
  }

  /** 2. Approve a request. No body. CRM contact is created in the background. */
  approve(id: string): Observable<SignupApiResponse> {
    return this.api.post(`${PROXY_BASE}/${id}/approve`, {});
  }

  /** 3. Reject a request. Reason is optional. */
  reject(id: string, reason?: string): Observable<SignupApiResponse> {
    return this.api.post(`${PROXY_BASE}/${id}/reject`, reason ? { reason } : {});
  }

  /** 4. Delete a request. HTTP 409 if crmSyncStatus is still pending — retry shortly. */
  delete(id: string): Observable<SignupApiResponse> {
    return this.api.delete(`${PROXY_BASE}/${id}`);
  }
}
